import { getAssignmentCounts } from '@macmillan-learning/gradebook-shared';
import dayjs from '../../../../../utils/dayjs';

export const PERFORMANCE_GRADE_THRESHOLD = 0.6;

/**
 * Checks if a date is from within the last week
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if the date is from within the last 7 days
 */
export const isDataFromLastWeek = (date) => {
  if (!date) {
    return false;
  }
  return dayjs(date).isAfter(dayjs().subtract(1, 'week'));
};

/**
 * Calculates primary metrics values for performance overview
 * @param {Array} allAssignments - Array of all assignments from categories
 * @param {Array} performanceData - Array of performance data points
 * @returns {Object} Object containing:
 *   - assignmentsCompleted: number of completed assignments
 *   - assignmentsPossible: total number of completed or missed assignments
 *   - missedAssignmentsSinceLastWeek: difference in missed assignments since last week
 */
export const calculatePrimaryMetrics = (allAssignments, performanceData) => {
  const {
    assignmentsCompleted: currentAssignmentsCompleted,
    assignmentsPossible: currentAssignmentsPossible,
  } = getAssignmentCounts(allAssignments);

  const currentMissedAssignments =
    currentAssignmentsPossible - currentAssignmentsCompleted;

  const previousPerformanceData = performanceData[performanceData.length - 1];

  let missedAssignmentsSinceLastWeek = null;
  let possibleAssignmentsSinceLastWeek = null;

  const hasDataFromPreviousWeek = isDataFromLastWeek(
    previousPerformanceData?.week_start_date,
  );

  if (hasDataFromPreviousWeek) {
    const {
      total_assignments_completed: previousAssignmentsCompleted,
      total_assignments_possible: previousAssignmentsPossible,
    } = previousPerformanceData;

    const previousMissedAssignments =
      previousAssignmentsPossible - previousAssignmentsCompleted;

    // Show the difference between the number of missed assignments this week and last week
    // It could be negative if the instructor overrides a missed assignments from the past,
    // So don't let it go below 0
    missedAssignmentsSinceLastWeek = Math.max(
      0,
      currentMissedAssignments - previousMissedAssignments,
    );

    // Calculate the difference between the number of possible assignments this week and last week
    // It could be negative if the instructor removes a assignment from the past,
    // So don't let it go below 0
    possibleAssignmentsSinceLastWeek = Math.max(
      0,
      currentAssignmentsPossible - previousAssignmentsPossible,
    );
  }

  return {
    assignmentsCompleted: currentAssignmentsCompleted,
    assignmentsPossible: currentAssignmentsPossible,
    missedAssignmentsSinceLastWeek,
    possibleAssignmentsSinceLastWeek,
  };
};

// Transforms performance data from database format to chart format
export const transformPerformanceData = (rawData, enrollmentStartDate) => {
  if (!rawData || rawData.length === 0) {
    return [];
  }

  // Calculate week numbers based on week_start_date relative to enrollment_start_date
  const enrollmentDate = new Date(enrollmentStartDate);

  // Transform each data point
  const transformed = rawData.map((point) => {
    const weekStartDate = new Date(point.week_start_date);

    // Calculate week number: difference in weeks from enrollment date
    const diffTime = weekStartDate - enrollmentDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const weekNumber = Math.ceil(diffDays / 7);

    return {
      weekNumber,
      classAverage: point.course_average_grade,
      studentGrade: point.grade,
    };
  });

  // Add week 0 as a duplicate of week 1 for connecting graph to y axis (if we have data)
  if (transformed.length > 0) {
    const week1Data =
      transformed.find((d) => d.weekNumber === 1) || transformed[0];
    transformed.unshift({
      weekNumber: 0,
      classAverage: week1Data.classAverage,
      studentGrade: week1Data.studentGrade,
    });
  }

  return transformed;
};

// Helper function to determine if the student's grade needs to switch colors
const gradeIsFollowingTrend = (isBelowThreshold, studentGrade) => {
  if (isBelowThreshold) {
    return studentGrade < PERFORMANCE_GRADE_THRESHOLD;
  }
  return studentGrade > PERFORMANCE_GRADE_THRESHOLD;
};

// Build series arrays (with nulls where each series doesn't have data)
// We need separate series for each color change, and we need them to have entries for all weekNumbers
// because the tooltips use the series index to determine which data point to highlight
export const buildDataSeries = (data) => {
  const dataSeries = [];
  let currentSeriesIndex = 0;
  let isBelowThreshold = data[0]?.studentGrade < PERFORMANCE_GRADE_THRESHOLD;

  // Initialize first series with nulls for all weekNumbers
  const createAlignedSeries = (isBelow, seriesId) =>
    data.map((originalPoint) => ({
      weekNumber: originalPoint.weekNumber,
      classAverage: originalPoint.classAverage,
      studentGrade: null,
      isBelowThreshold: isBelow,
      seriesId: seriesId,
    }));

  dataSeries.push(createAlignedSeries(isBelowThreshold, currentSeriesIndex));

  data.forEach((i, index) => {
    if (i.studentGrade === null || i.studentGrade === undefined) {
      return;
    }

    // Check if the NEXT point will cause a trend change
    const nextPoint = index < data.length - 1 ? data[index + 1] : null;
    const willChangeTrend =
      nextPoint &&
      nextPoint.studentGrade &&
      !gradeIsFollowingTrend(isBelowThreshold, nextPoint.studentGrade);

    // If trend will change on next point, do the following:
    if (willChangeTrend) {
      // Add current point to current series
      dataSeries[currentSeriesIndex][index] = {
        weekNumber: i.weekNumber,
        classAverage: i.classAverage,
        studentGrade: i.studentGrade,
        isBelowThreshold: isBelowThreshold,
        isFirstPointAfterThresholdChange: false,
        seriesId: currentSeriesIndex,
      };

      // Start new series before threshold is crossed
      currentSeriesIndex++;
      isBelowThreshold = !isBelowThreshold;
      dataSeries.push(
        createAlignedSeries(isBelowThreshold, currentSeriesIndex),
      );

      // Add current point to NEW series with NEW color
      dataSeries[currentSeriesIndex][index] = {
        weekNumber: i.weekNumber,
        classAverage: i.classAverage,
        studentGrade: i.studentGrade,
        isBelowThreshold: isBelowThreshold,
        isFirstPointAfterThresholdChange: true,
        seriesId: currentSeriesIndex,
      };
    } else {
      // Add point to current series as normal
      dataSeries[currentSeriesIndex][index] = {
        weekNumber: i.weekNumber,
        classAverage: i.classAverage,
        studentGrade: i.studentGrade,
        isBelowThreshold: isBelowThreshold,
        isFirstPointAfterThresholdChange: false,
        seriesId: currentSeriesIndex,
      };
    }
  });

  // Filter out empty series
  const filteredDataSeries = dataSeries.filter((series) =>
    series.some((point) => point.studentGrade !== null),
  );

  return filteredDataSeries;
};
